import { useState, useEffect } from 'react'

const EMPTY = {
  company: '', contact: '', email: '', phone: '',
  industry: 'Technology', category: '', revenue: '', employees: '',
  location: '', score: '', source: 'Cold Outreach',
  status: 'New', last_contacted: '', notes: '', website: '',
}

const INDUSTRIES = ['Technology','Healthcare','Finance','Retail','Manufacturing','Real Estate','Legal','Other']
const SOURCES    = ['Cold Outreach','Referral','LinkedIn','Inbound','Event','Partner']
const STATUSES   = ['New','Qualified','Proposal','Negotiation','Closed Lost','Cold']

export default function ProspectModal({ prospect, onSave, onClose }) {
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (prospect) {
      setForm({
        company:        prospect.company || '',
        contact:        prospect.contact || '',
        email:          prospect.email || '',
        phone:          prospect.phone || '',
        industry:       prospect.industry || 'Technology',
        category:       prospect.category || '',
        revenue:        prospect.revenue ?? '',
        employees:      prospect.employees ?? '',
        location:       prospect.location || '',
        score:          prospect.score ?? '',
        source:         prospect.source || 'Cold Outreach',
        status:         prospect.status || 'New',
        last_contacted: prospect.last_contacted || '',
        notes:          prospect.notes || '',
        website:        prospect.website || '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [prospect])

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }))

  const handleSave = async () => {
    if (!form.company.trim() || !form.contact.trim()) {
      setError('Company and contact name are required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave({
        ...form,
        revenue:   parseInt(form.revenue) || 0,
        employees: parseInt(form.employees) || 0,
        score:     Math.min(100, Math.max(0, parseInt(form.score) || 0)),
        last_contacted: form.last_contacted || null,
      })
      onClose()
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save prospect.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>{prospect ? 'Edit prospect' : 'Add prospect'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.grid}>
          <Field label="Company *">
            <input value={form.company} onChange={(e) => set('company', e.target.value)} />
          </Field>
          <Field label="Contact name *">
            <input value={form.contact} onChange={(e) => set('contact', e.target.value)} />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(602) 555-0100" />
          </Field>
          <Field label="Website" full>
            <input
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="https://example.com"
            />
          </Field>
          <Field label="Industry">
            <select value={form.industry} onChange={(e) => set('industry', e.target.value)}>
              {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Dentist, Auto Repair" />
          </Field>
          <Field label="Annual revenue ($)">
            <input type="number" value={form.revenue} onChange={(e) => set('revenue', e.target.value)} placeholder="e.g. 2400000" />
          </Field>
          <Field label="Employees">
            <input type="number" value={form.employees} onChange={(e) => set('employees', e.target.value)} />
          </Field>
          <Field label="Location">
            <input value={form.location} onChange={(e) => set('location', e.target.value)} />
          </Field>
          <Field label="Lead score (0–100)">
            <input type="number" min="0" max="100" value={form.score} onChange={(e) => set('score', e.target.value)} />
          </Field>
          <Field label="Source">
            <select value={form.source} onChange={(e) => set('source', e.target.value)}>
              {SOURCES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => set('status', e.target.value)}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Last contacted">
            <input type="date" value={form.last_contacted} onChange={(e) => set('last_contacted', e.target.value)} />
          </Field>
          <Field label="Notes" full>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              style={styles.textarea}
            />
          </Field>
        </div>

        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save prospect'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, full }) {
  return (
    <div style={{ ...styles.field, ...(full ? styles.fieldFull : {}) }}>
      <label style={styles.fieldLabel}>{label}</label>
      {children}
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    border: '0.5px solid #e0dfd8',
    padding: '1.5rem',
    width: 520,
    maxWidth: '95vw',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  title: { fontSize: 16, fontWeight: 500, color: '#1a1a1a' },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#888', padding: 4 },
  error: {
    background: '#FCEBEB', color: '#791F1F',
    border: '0.5px solid #F09595',
    borderRadius: 8, padding: '10px 14px',
    fontSize: 13, marginBottom: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldFull: { gridColumn: '1 / -1' },
  fieldLabel: { fontSize: 11, color: '#666' },
  textarea: {
    resize: 'vertical',
    minHeight: 72,
    padding: 8,
    fontFamily: 'inherit',
    fontSize: 13,
    border: '0.5px solid #ccc',
    borderRadius: 8,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' },
  cancelBtn: {
    background: 'none', border: '0.5px solid #ccc',
    borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#444',
  },
  saveBtn: {
    background: '#1a1a1a', color: '#fff', border: 'none',
    borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
}
